import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Section from '../components/ui/Section';
import SafeIcon from '../common/SafeIcon';
import Button from '../components/ui/Button';
import * as FaIcons from 'react-icons/fa';

const ProgramBlock = ({ title, icon, desc, details, image, reverse }) => (
  <motion.div 
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8 }}
    className={`flex flex-col lg:flex-row gap-16 items-center mb-32 ${reverse ? 'lg:flex-row-reverse' : ''}`}
  >
    <div className="flex-1 space-y-8">
      <div className="inline-flex items-center gap-3 px-4 py-2 bg-primary/5 rounded-full text-primary font-bold text-sm tracking-wide uppercase">
        <SafeIcon icon={icon} /> Program Focus
      </div>
      <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary-dark">{title}</h2>
      <p className="text-gray-600 text-lg leading-relaxed">{desc}</p>
      
      <div className="bg-surface p-8 rounded-2xl border border-gray-100">
        <h4 className="font-bold text-gray-900 mb-4">Key Impact Areas:</h4>
        <ul className="space-y-4">
          {details.map((item, idx) => (
            <li key={idx} className="flex items-start gap-3 text-gray-700">
              <div className="mt-1 w-5 h-5 bg-secondary rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">
                <SafeIcon icon={FaIcons.FaCheck} />
              </div>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
    
    <div className="flex-1 w-full relative">
      <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-video group">
        <div className="absolute inset-0 bg-primary-dark/20 group-hover:bg-transparent transition-all duration-500 z-10"></div>
        <img src={image} alt={`${title} in action`} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" />
      </div>
      {/* Decorative dots */}
      <div className={`absolute -bottom-6 -right-6 w-32 h-32 bg-[url('https://www.transparenttextures.com/patterns/dots.png')] opacity-20 -z-10 ${reverse ? 'left-auto right-auto -left-6' : ''}`}></div>
    </div>
  </motion.div>
);

const Programs = () => {
  return (
    <>
      <Helmet>
        <title>Our Programs | AGIR USA</title>
        <meta name="description" content="Discover how we support children through meals, education, spiritual growth, and advocacy." />
      </Helmet>

      {/* Hero Section */}
      <div className="relative py-32 md:py-48 flex items-center justify-center overflow-hidden min-h-[60vh]">
        <div className="absolute inset-0">
          <motion.img 
             initial={{ scale: 1.1 }}
             animate={{ scale: 1 }}
             transition={{ duration: 1.5 }}
            src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/7ab728fa003eb0944fc5dd69f70d57d4.jpeg" 
            alt="Students engaging in learning" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary-dark/80 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-dark to-transparent"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-5xl md:text-7xl font-bold mb-6">Our Programs</h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-2xl mx-auto leading-relaxed font-light">
              Holistic care for the <span className="text-secondary italic">body, mind, and spirit.</span>
            </p>
          </motion.div>
        </div>
      </div>

      <Section>
        {/* Daily Meals Program */}
        <ProgramBlock 
          title="Daily Meals Program" 
          icon={FaIcons.FaBowlFood} 
          desc="Hunger is the greatest barrier to education. Our feeding program ensures that over 1,000 children receive a hot, nutritious meal every school day. For many, this is their only substantial meal of the day."
          details={[
            "Over 3 million meals served to date",
            "Locally sourced ingredients to support farmers",
            "Nutritionally balanced for growing children",
            "Cooking staff employed from the local community"
          ]}
          image="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/dc7eb88b5901d7044887350acce3d65c.jpeg"
        />

        {/* Education & School Support */}
        <ProgramBlock 
          title="Education & School Support" 
          icon={FaIcons.FaBookOpen} 
          reverse={true}
          desc="We believe education is the key to breaking the cycle of poverty. We provide tuition assistance, school supplies, and maintain safe learning environments."
          details={[
            "Tuition coverage for vulnerable children",
            "Distribution of backpacks, books, and uniforms",
            "Teacher training and salary support",
            "School infrastructure improvements"
          ]}
          image="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/1485015ec55730083f0618a640d8fc98.jpeg"
        />

        {/* Spiritual Support */}
        <ProgramBlock 
          title="Spiritual Support" 
          icon={FaIcons.FaHeart} 
          desc="As a Christ-centered organization, we share the hope of the Gospel through action and word. We foster a supportive environment where children learn values of love, forgiveness, and service."
          details={[
            "Weekly Bible clubs and youth groups",
            "Pastoral care for families in crisis",
            "Character development curriculum",
            "Partnering with local churches"
          ]}
          image="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/e9bc74681ffe16a90563305596198f1c.jpeg"
        />
      </Section>
      
      {/* CTA Section */}
      <Section bg="dark" className="text-center py-24">
        <h2 className="font-heading text-4xl font-bold text-white mb-6">Support These Programs</h2>
        <p className="text-gray-300 max-w-2xl mx-auto mb-10 text-lg">Your monthly gift ensures these lights stay on and plates stay full.</p>
        <div className="flex justify-center gap-4">
           <Button to="/get-involved" variant="secondary" size="lg" className="px-12">Donate Now</Button>
        </div>
      </Section>
    </>
  );
};

export default Programs;